import React from 'react'
import { Button } from '../simple/button'
import { IconDownload, IconTrash } from '../../icons/library'
import { AudioPlayer } from '../simple/audio'
import { useAnimationFrame } from '../../hooks/hooks'
import { FloatingLoader } from '../simple/loader'
import axios from 'axios'
import { showAxiosErrorAsToast } from '../../request'
import { Toasts } from '../../providers/toast'
import { Popups } from '../../providers/popups'

export type Recording = {
  transcript?: string
  serverID: string
  audioUrl: string
}

interface AudioRecorderProps {
  /** The button content to show in the 'start recording' state */
  children: React.ReactNode
  /** Callback function to extract the transcript from the recording */
  extractTranscript?: (transcript: string, overwrite: boolean) => void
  /** Tooltip text to show when hovering over the `start recording` button */
  tooltip?: string
  /** The current recording */
  recording?: Recording
  /** Callback function to set the current recording */
  setRecording: React.Dispatch<React.SetStateAction<Recording | undefined>>
  /** Callback function to tell the parent component that the recorder is busy */
  setRecorderBusy: React.Dispatch<React.SetStateAction<boolean>>
  /** Additional class names for styling */
  className?: string
}

/**
 * Audio recorder button component that allows the user to record audio, uploads it to the server, and provides playback functionality.
 */
export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  children,
  extractTranscript,
  tooltip,
  recording,
  setRecording,
  className,
  setRecorderBusy,
}) => {
  const [currentlyRecording, setCurrentlyRecording] = React.useState(false)
  const [actuallyRecording, setActuallyRecording] = React.useState(false)
  const [recordingTimeString, setRecordingTimeString] = React.useState('')
  const [errorRecording, setErrorRecording] = React.useState<string | undefined>(undefined)
  const [loading, setLoading] = React.useState(false)

  const volDotRef = React.useRef<HTMLDivElement>(null)
  const analyserNode = React.useRef<AnalyserNode>()
  const mediaRecorderRef = React.useRef<MediaRecorder>()
  const recordingTimerRef = React.useRef<number>(0)

  const countdownStart = -3.5
  // wait a tiny bit so we dont pick up the end of the last beep
  const startRecordingAt = 0.25

  const computeRecordingTimeString = () => {
    const time = recordingTimerRef.current
    if (time < 0) {
      setRecordingTimeString(Math.ceil(-time).toString() + '...')
    } else {
      setRecordingTimeString(timeToCallString(time))
    }
  }

  const reset = () => {
    setCurrentlyRecording(false)
    setRecording(undefined)
    setErrorRecording(undefined)
    setLoading(false)
    setRecorderBusy(false)
  }

  const startRecording = async () => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setErrorRecording('Your browser does not support recording audio')
      return
    }
    await navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const audioContext = new AudioContext()
        const mediaStreamAudioSourceNode = audioContext.createMediaStreamSource(stream)
        analyserNode.current = audioContext.createAnalyser()
        mediaStreamAudioSourceNode.connect(analyserNode.current)
        mediaRecorderRef.current = new MediaRecorder(stream)
        const audioChunks: Blob[] = []
        mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
          audioChunks.push(event.data)
        })
        mediaRecorderRef.current.addEventListener('stop', () => {
          setCurrentlyRecording(false)
          setLoading(true)

          stream.getTracks().forEach((track) => track.stop())

          const audioBlob = new Blob(audioChunks, { type: 'audio/mp3' })
          const formdata = new FormData()
          formdata.append('audio', audioBlob, 'recording.mp3')
          axios<{
            id: string
            audio_url: string
            transcription: string
          }>({
            url: '/uploadRecording',
            data: formdata,
            method: 'POST',
          })
            .then((data) => data.data)
            .then((data) => {
              let transcription = data.transcription
              if (!transcription) {
                transcription = ''
                Toasts.showToast({
                  subject: 'Transcription Empty',
                  message:
                    'Please write your message. A message is needed for recipients who receive this message as email or text, and also for those who receive it in a different language.',
                  duration: 10000,
                })
              } else if (transcription === 'Transcription failed, please write your message.') {
                transcription = ''
                Popups.confirm({
                  title: <b>Auto-Transcription Failed</b>,
                  message:
                    'Your recording may have been too long to automatically transcribe. Please write your message. A message is needed for recipients who receive this message as email or text, and also for those who receive it in a different language.',
                  yesText: 'OK',
                })
              }

              setRecording({
                serverID: data.id,
                audioUrl: data.audio_url,
                transcript: transcription,
              })
              if (transcription && extractTranscript) extractTranscript(transcription, false)
            })
            .catch((err) => {
              showAxiosErrorAsToast(err)
              setErrorRecording('Failed to upload recording')
            })
            .finally(() => {
              setLoading(false)
              setRecorderBusy(false)
            })
        })

        // ensure the audio context is resumed before starting the recording
        audioContext.resume().then(() => {
          setRecorderBusy(true)
          setCurrentlyRecording(true)
          setActuallyRecording(false)
          recordingTimerRef.current = countdownStart
          computeRecordingTimeString()
          const firstBeep = Math.ceil(countdownStart)
          for (let i = firstBeep; i <= 0; i++) {
            const pitch = i === 0 ? 700 : 440
            const msUntil = (i - countdownStart) * 1000
            setTimeout(() => beep(200, pitch, 10, audioContext), msUntil)
          }
        })
      })
      .catch(() => {
        setErrorRecording('Failed to access microphone')
      })
  }

  useAnimationFrame((deltaTime) => {
    if (!mediaRecorderRef.current) return
    const prevTime = recordingTimerRef.current
    const newTime = prevTime + deltaTime
    if (prevTime < startRecordingAt && newTime >= startRecordingAt) {
      mediaRecorderRef.current.start()
      setActuallyRecording(true)
    }
    recordingTimerRef.current = newTime
    computeRecordingTimeString()

    if (analyserNode.current && volDotRef.current) {
      const pcmData = new Float32Array(analyserNode.current.fftSize)
      analyserNode.current.getFloatTimeDomainData(pcmData)
      const sumSquares = pcmData.reduce((sum, amplitude) => sum + amplitude * amplitude, 0)
      let size = Math.pow(sumSquares / pcmData.length, 1 / 4) * 60 + 10
      let lastSize = volDotRef.current.offsetWidth
      size = Math.round((lastSize * 3 + size) / 4)
      volDotRef.current.style.width = `${size}px`
      volDotRef.current.style.height = `${size}px`
    }
  }, currentlyRecording)

  return (
    <div className={`relative flex max-h-[34px] min-h-[34px] items-center gap-2`}>
      {loading && <FloatingLoader color="text-slate-300" background="bg-white/50" />}
      {errorRecording ? (
        <>
          <div className="font-bold text-red-500">{errorRecording}</div>
          <Button onClick={reset}>OK</Button>
        </>
      ) : recording ? (
        <>
          <AudioPlayer className="h-[33.59px]" src={recording.audioUrl} />
          <Button onClick={reset} tooltip="Clear recording">
            <IconTrash />
          </Button>
          {extractTranscript && recording.transcript && (
            <Button
              onClick={() => extractTranscript(recording.transcript!, true)}
              tooltip="Replace your message with recording transcript"
            >
              <IconDownload />
            </Button>
          )}
        </>
      ) : currentlyRecording ? (
        <>
          <div className="fixed inset-0 z-40 bg-slate-500/25" />
          <div className="z-50 -m-1 flex max-h-[34px] min-h-[34px] items-center gap-2 rounded-lg bg-white py-1 pl-2 pr-1">
            {actuallyRecording && (
              <div className="relative flex h-[33px] w-[33px] items-center justify-center">
                <div className="max-h-full max-w-full rounded-full bg-red-500 opacity-50" ref={volDotRef}></div>
                <div className="absolute h-[13px] w-[13px] rounded-full bg-red-500"></div>
              </div>
            )}
            Recording
            <b className="min-w-14">{recordingTimeString}</b>
            {actuallyRecording && (
              <Button
                logo
                small
                onClick={() => {
                  if (mediaRecorderRef.current) mediaRecorderRef.current.stop()
                }}
              >
                Done
              </Button>
            )}
          </div>
        </>
      ) : (
        <Button tooltip={tooltip} onClick={startRecording} className={className}>
          {children}
        </Button>
      )}
    </div>
  )
}

function beep(duration: number, frequency: number, volume: number, audioContext: AudioContext): void {
  const oscillatorNode = audioContext.createOscillator()
  const gainNode = audioContext.createGain()
  oscillatorNode.connect(gainNode)

  // Set the oscillator frequency in hertz
  oscillatorNode.frequency.value = frequency

  // Set the type of oscillator
  oscillatorNode.type = 'sine'
  gainNode.connect(audioContext.destination)

  // Set the gain to the volume
  gainNode.gain.value = volume * 0.01
  const end = audioContext.currentTime + duration * 0.001
  gainNode.gain.setValueAtTime(volume * 0.01, end - 0.02)
  gainNode.gain.linearRampToValueAtTime(0, end)
  // Start audio with the desired duration
  oscillatorNode.start(audioContext.currentTime)
  oscillatorNode.stop(end)
}

export const timeToCallString = (time: number): string => {
  const minutes = Math.floor(time / 60)
    .toString()
    .padStart(1, '0')
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, '0')
  return `${minutes}:${seconds}`
}
